import "./AmountChooserInfoPanel.scss";
import React, { useState } from "react";
import { IntlPackagingType } from "./AmountChooserInfoPackaging";
import { FormattedNumber, FormattedMessage } from "react-intl";

import { calculatePiecesFromWeight, preservePrecision } from "./AmountChooserInfoUtil";

// Default duration in ms to temporarily display the info panel
const DEFAULT_TIMEOUT_STANDARD = 3000;
// Default display duration in ms after not showing it permanently anymore
// (avoids flickering when quickly going from permanent to timed display,
// e.g., when clicking increase amount button while focus inside input field)
const DEFAULT_TIMEOUT_SHORT = 200;

export type AmountChooserInfoPanelProps = {
	numberOfPieces?: number;
	totalWeight?: number;
	weightPerPieceValue: number;
	packagingTypeId?: string | null;
	totalPrice?: number | null;
	currency?: string | null;
	isPriceLoading?: boolean;
	showIfZeroAmount?: boolean;
	showPermanently?: boolean;
	showTimedInitially?: boolean;
	timeoutStandard?: number;
	timeoutShort?: number;
	className?: string | null;
};

const getWeightFromProps = (totalWeight?: number, numberOfPieces?: number, weightPerPieceValue?: number) => {
	if (numberOfPieces === undefined || weightPerPieceValue === undefined) {
		return totalWeight || 0;
	}

	// number of pieces prop has higher priority than weight prop
	const newWeight = preservePrecision(numberOfPieces, weightPerPieceValue, numberOfPieces * weightPerPieceValue);

	return newWeight || 0;
};

type Visibility =
	| { state: "HIDDEN"; timeoutId?: any }
	| { state: "TIMED"; timeoutId: any }
	| { state: "PERMANENT"; timeoutId?: any };

/**
 * Component that renders the amount chooser info panel for
 articles with a weight per piece.
 */
export const AmountChooserInfoPanel = ({
	showTimedInitially,
	showPermanently,
	showIfZeroAmount,
	timeoutShort,
	timeoutStandard,
	totalWeight,
	numberOfPieces,
	weightPerPieceValue,
	packagingTypeId,
	totalPrice,
	currency,
	isPriceLoading,
	className,
}: AmountChooserInfoPanelProps) => {
	const [currentWeight, setCurrentWeight] = useState(
		getWeightFromProps(totalWeight, numberOfPieces, weightPerPieceValue),
	);
	const [visibility, setVisibility] = useState<Visibility>({ state: "HIDDEN" });

	const changeVisibility = (newVisibility) => {
		if (visibility.timeoutId) {
			clearTimeout(visibility.timeoutId);
		}
		setVisibility(newVisibility);
	};

	React.useEffect(() => {
		if (showTimedInitially) {
			setVisibility({
				state: "TIMED",
				timeoutId: setTimeout(() => {
					setVisibility({ state: "HIDDEN" });
				}, timeoutStandard || DEFAULT_TIMEOUT_STANDARD),
			});
		}
	}, [setVisibility]);

	React.useEffect(() => {
		const newWeight = getWeightFromProps(totalWeight, numberOfPieces, weightPerPieceValue);
		const didWeightChange = currentWeight !== newWeight;
		if (didWeightChange) {
			setCurrentWeight(newWeight);
		}

		if (!showIfZeroAmount && newWeight === 0) {
			return changeVisibility({ state: "HIDDEN" });
		}

		if (showPermanently && visibility.state !== "PERMANENT") {
			return changeVisibility({ state: "PERMANENT" });
		}

		if (!showPermanently && (visibility.state === "PERMANENT" || didWeightChange)) {
			const timeout = didWeightChange
				? timeoutStandard || DEFAULT_TIMEOUT_STANDARD
				: timeoutShort || DEFAULT_TIMEOUT_SHORT;

			changeVisibility({
				state: "TIMED",
				timeoutId: setTimeout(() => {
					setVisibility({ state: "HIDDEN" });
				}, timeout),
			});
		}
	}, [totalWeight, numberOfPieces, weightPerPieceValue, showIfZeroAmount, showPermanently, setCurrentWeight]);

	if (visibility.state === "HIDDEN") {
		return null;
	}

	const piecesFromWeight = calculatePiecesFromWeight(currentWeight, weightPerPieceValue);

	const piecesString = <IntlPackagingType id={packagingTypeId} pieces={piecesFromWeight} />;

	const weightString = currentWeight ? (
		<span data-testid="test-amountchooser-infopanel-weight">
			<FormattedNumber value={currentWeight} format="decimal" /> <FormattedMessage id="ORC_KG" />
		</span>
	) : null;

	const priceString =
		!isPriceLoading && totalPrice && currency ? (
			<FormattedNumber value={totalPrice} format="currency" currency={currency} />
		) : (
			<span>
				<i className="icon-spinner" data-testid="test-amountchooser-infopanel-price-spinner" />
			</span>
		);
	const baseClassName = "amountchooser-infopanel amountchooser-infopanel--absoluteposition";
	const fullClassName = className ? baseClassName + " " + className : baseClassName;

	return (
		<div className={fullClassName} data-testid="test-amountchooser-infopanel">
			<div className="amountchooser-infopanel-content background-info border-info">
				{piecesString}
				{weightString}
				{priceString}
			</div>
		</div>
	);
};
